import { getLocalizedString } from "../getLocalizedString.js";
import type { DynamicValue, FieldMetadata, FilterFieldType, FilterOperator, FilterValueType } from "./types.js";

/**
 * Operators that take no value at all.
 *
 * `eOperators.ParameterLessChecks` plus the blank checks.
 */
const PARAMETERLESS_OPERATORS: FilterOperator[] = ["isnull", "isnotnull", "istrue", "isfalse", "isblank", "isnotblank"];

/** Operators whose value is a two element `[from, to]` array. */
const PAIR_OPERATORS: FilterOperator[] = ["between", "notbetween", "datebetween", "datenotbetween"];

/** Operators whose value is an array of values. */
const LIST_OPERATORS: FilterOperator[] = ["inlist", "notinlist"];

/** How many values an operator needs. */
export type OperatorArity = "none" | "single" | "pair" | "list";

export function getOperatorArity(operator: FilterOperator): OperatorArity {
	if (PARAMETERLESS_OPERATORS.includes(operator)) return "none";
	if (PAIR_OPERATORS.includes(operator)) return "pair";
	if (LIST_OPERATORS.includes(operator)) return "list";
	return "single";
}

/**
 * Operator labels, keyed by operator. Comparison operators have a different
 * label for dates than for numbers, matching the Windows filter editor.
 *
 * @see `appframe/Appframe3.Win/Controls/afFilterEditor/Utils/FilterLineControlUtils.vb`
 */
const OPERATOR_LABELS: Record<FilterOperator, string> = {
	equals: "Equal to",
	notequals: "Not equal to",
	greaterthan: "Greater than",
	greaterthanorequal: "Greater than or equal to",
	lessthan: "Less than",
	lessthanorequal: "Less than or equal to",
	beginswith: "Begins with",
	notbeginswith: "Does not begin with",
	endswith: "Ends with",
	notendswith: "Does not end with",
	contains: "Contains",
	notcontains: "Does not contain",
	like: "Like",
	notlike: "Not like",
	isnull: "Is null",
	isnotnull: "Is not null",
	isblank: "Is empty",
	isnotblank: "Is not empty",
	istrue: "Is true",
	isfalse: "Is false",
	inlist: "In list",
	notinlist: "Not in list",
	between: "Between",
	notbetween: "Not between",
	dateequals: "Equal to",
	notdatequals: "Not equal to",
	dategreaterthan: "After",
	dategreaterthanorequal: "On or after",
	datelessthan: "Before",
	datelessthanorequal: "Up to",
	datebetween: "Between",
	datenotbetween: "Not between",
};

/** Comparison operator labels used for date and time fields. */
const DATE_OPERATOR_LABELS: Partial<Record<FilterOperator, string>> = {
	greaterthan: "After",
	greaterthanorequal: "On or after",
	lessthan: "Before",
	lessthanorequal: "Up to",
};

/**
 * Returns the localized label for an operator. Date fields get the
 * "After"/"Before" wording instead of "Greater than"/"Less than".
 */
export function getOperatorLabel(operator: FilterOperator, fieldType?: FilterFieldType): string {
	let isDate = fieldType === "date" || fieldType === "datetime";
	let label = (isDate ? DATE_OPERATOR_LABELS[operator] : undefined) ?? OPERATOR_LABELS[operator];

	return getLocalizedString(label ?? operator);
}

const EQUALITY: FilterOperator[] = ["equals", "notequals"];
const LIST_CHECKS: FilterOperator[] = ["inlist", "notinlist"];
const RANGE: FilterOperator[] = ["between", "notbetween"];
const COMPARISONS: FilterOperator[] = ["greaterthan", "greaterthanorequal", "lessthan", "lessthanorequal"];
const STRING_CHECKS: FilterOperator[] = [
	"contains",
	"notcontains",
	"beginswith",
	"notbeginswith",
	"endswith",
	"notendswith",
	"isblank",
	"isnotblank",
];

/**
 * Operators offered per field type, in the order the Windows filter editor
 * presents them.
 */
const OPERATORS_BY_TYPE: Record<FilterFieldType, FilterOperator[]> = {
	date: [...EQUALITY, ...LIST_CHECKS, ...RANGE, ...COMPARISONS],
	datetime: [...EQUALITY, ...LIST_CHECKS, ...RANGE, ...COMPARISONS],
	number: [...EQUALITY, ...LIST_CHECKS, ...RANGE, ...COMPARISONS],
	boolean: ["istrue", "isfalse"],
	string: [...EQUALITY, ...STRING_CHECKS, ...LIST_CHECKS, ...RANGE, ...COMPARISONS],
	uniqueidentifier: [...EQUALITY, ...STRING_CHECKS, ...LIST_CHECKS, ...RANGE, ...COMPARISONS],
};

/**
 * Default operator per field type.
 *
 * Text defaults to `contains` because a substring search is what people
 * reach for when they type into a filter box without picking an operator —
 * `beginswith` silently misses matches in the middle of the value.
 */
const DEFAULT_OPERATOR_BY_TYPE: Record<FilterFieldType, FilterOperator> = {
	date: "equals",
	datetime: "equals",
	number: "equals",
	boolean: "istrue",
	string: "contains",
	uniqueidentifier: "beginswith",
};

/**
 * Returns the operators available for a field. Nullable fields additionally get
 * the null checks. Boolean fields never get null checks, since `istrue` and
 * `isfalse` already cover the parameterless cases.
 */
export function getOperatorsForField(field: Pick<FieldMetadata, "type" | "nullable">): FilterOperator[] {
	let operators = [...(OPERATORS_BY_TYPE[field.type] ?? OPERATORS_BY_TYPE.string)];

	if (field.nullable && field.type !== "boolean") {
		operators.push("isnull", "isnotnull");
	}

	return operators;
}

export function getDefaultOperator(field: Pick<FieldMetadata, "type">): FilterOperator {
	return DEFAULT_OPERATOR_BY_TYPE[field.type] ?? "equals";
}

/**
 * The `valueType` to send for a field/operator combination.
 *
 * Parameterless operators send `"null"`, list and range operators send
 * `"array"`, everything else sends the field's own value type.
 */
export function getValueType(field: Pick<FieldMetadata, "type">, operator: FilterOperator): FilterValueType {
	if (getOperatorArity(operator) === "none") return "null";

	switch (field.type) {
		case "number":
			return "number";
		case "boolean":
			return "boolean";
		case "date":
		case "datetime":
			return "datetime";
		case "uniqueidentifier":
			return "uniqueidentifier";
		default:
			return "string";
	}
}

/** Relative date values offered in the date value editor, in menu order. */
export const RELATIVE_DATE_VALUES: DynamicValue[] = [
	"Yesterday",
	"Today",
	"Tomorrow",
	"ThisWeek",
	"ThisMonth",
	"ThisYear",
	"LastWeek",
	"LastMonth",
	"LastYear",
	"NextWeek",
	"NextMonth",
	"NextYear",
];

const DYNAMIC_VALUE_LABELS: Record<DynamicValue, string> = {
	CurrentUser: "Current user",
	Yesterday: "Yesterday",
	Today: "Today",
	Tomorrow: "Tomorrow",
	ThisWeek: "This week",
	ThisMonth: "This month",
	ThisYear: "This year",
	LastWeek: "Last week",
	LastMonth: "Last month",
	LastYear: "Last year",
	NextWeek: "Next week",
	NextMonth: "Next month",
	NextYear: "Next year",
	UserYesterday: "Yesterday",
	UserToday: "Today",
	UserTomorrow: "Tomorrow",
	UserThisWeek: "This week",
	UserThisMonth: "This month",
	UserThisYear: "This year",
	UserLastWeek: "Last week",
	UserLastMonth: "Last month",
	UserLastYear: "Last year",
	UserNextWeek: "Next week",
	UserNextMonth: "Next month",
	UserNextYear: "Next year",
};

export function getDynamicValueLabel(value: DynamicValue): string {
	return getLocalizedString(DYNAMIC_VALUE_LABELS[value] ?? value);
}

/**
 * Returns the dynamic ("special") values that can be used with a field. Date
 * fields get the relative dates; string fields can be compared to the current
 * user name.
 */
export function getDynamicValuesForField(field: Pick<FieldMetadata, "type">): DynamicValue[] {
	if (field.type === "date" || field.type === "datetime") return [...RELATIVE_DATE_VALUES];
	if (field.type === "string") return ["CurrentUser"];

	return [];
}

/**
 * Aliases for names `eOperators.ToString()` can produce that are not operators
 * in their own right. `Not` is the bare negation flag (i.e. "not equal to"),
 * `Tweens` shares its numeric value with `NotBetween`, and combined flags are
 * rendered as a comma separated list.
 *
 * @see `FilterElementConverter.FilterExpressionToDictionary`
 */
const OPERATOR_ALIASES: Record<string, FilterOperator> = {
	not: "notequals",
	tweens: "notbetween",
	notdate: "notdatequals",
	// The enum member is `NotDateEquals`, but the wire format drops an "e".
	notdateequals: "notdatequals",
};

/**
 * Normalizes an operator string coming from the server or from another filter
 * producer (`AfGrid` emits PascalCase names) to the canonical lower-case form.
 */
export function normalizeOperator(operator: string): FilterOperator {
	let normalized = operator.toLowerCase().replace(/[\s,]/g, "");

	return OPERATOR_ALIASES[normalized] ?? (normalized as FilterOperator);
}
